import { Alert, Checkbox, Empty, Input, Popconfirm, Table, Typography, message } from 'antd'
import type { ColumnsType, ColumnGroupType, ColumnType } from 'antd/es/table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { platformGetTenantEntitlementMatrix, platformUpdateEntitlements } from '../api'
import type { TenantFeatureEntitlementMatrixRow, TenantFeatureGroupDef } from '../types'
import { SectionCard } from './adminShared'
import './platform-tables.css'
import './tenant-entitlement-matrix.css'

const { Search } = Input

/**
 * VISTOERA_SAAS_TENANT_FEATURE_ENTITLEMENT_MATRIX_V1 — "租户套餐" (Platform
 * Admin manually grants/revokes Vistoera product capabilities per tenant;
 * spec explicitly excludes payment/credits/quotas/plan pricing this phase).
 *
 * Reuses the existing single-workspace entitlement write endpoint
 * (`platformUpdateEntitlements` -> `PATCH /platform/workspaces/{id}/
 * entitlements` -> `entitlement_service.set_many`) for every checkbox
 * toggle — this page only adds the cross-tenant paginated/searchable read
 * (`platformGetTenantEntitlementMatrix`) and the matrix UI around it.
 */
export default function TenantEntitlementMatrixPage() {
  const [groups, setGroups] = useState<TenantFeatureGroupDef[]>([])
  const [rows, setRows] = useState<TenantFeatureEntitlementMatrixRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Tracks which (workspace_id, feature_key) checkboxes currently have an
  // in-flight save, so we can show a per-cell spinner-ish disabled state
  // and never let a second click race the first (spec §13).
  const [pending, setPending] = useState<Record<string, boolean>>({})
  const reqSeq = useRef(0)

  const load = useCallback(() => {
    const seq = ++reqSeq.current
    setLoading(true)
    setError(null)
    platformGetTenantEntitlementMatrix({ page, page_size: pageSize, search: search || undefined })
      .then((r) => {
        if (seq !== reqSeq.current) return
        setGroups(r.data.groups)
        setRows(r.data.items)
        setTotal(r.data.total)
      })
      .catch((err) => {
        if (seq !== reqSeq.current) return
        setError((err as Error).message)
      })
      .finally(() => {
        if (seq !== reqSeq.current) return
        setLoading(false)
      })
  }, [page, pageSize, search])

  useEffect(load, [load])

  const onSearch = (value: string) => {
    setPage(1)
    setSearch(value.trim())
  }

  const toggleFeature = async (row: TenantFeatureEntitlementMatrixRow, featureKey: string, nextValue: boolean) => {
    const cellKey = `${row.workspace_id}:${featureKey}`
    if (pending[cellKey]) return

    // Optimistic update (spec §13) — rolled back on failure below.
    setRows((prev) =>
      prev.map((r) =>
        r.workspace_id === row.workspace_id
          ? { ...r, entitlements: { ...r.entitlements, [featureKey]: nextValue } }
          : r,
      ),
    )
    setPending((p) => ({ ...p, [cellKey]: true }))
    try {
      await platformUpdateEntitlements(row.workspace_id, { [featureKey]: nextValue })
    } catch (err) {
      // Rollback — never leave the UI showing "granted" when the server
      // write actually failed (spec §13: "禁止出现 UI 显示已授权，但服务器
      // 实际没有保存成功").
      setRows((prev) =>
        prev.map((r) =>
          r.workspace_id === row.workspace_id
            ? { ...r, entitlements: { ...r.entitlements, [featureKey]: !nextValue } }
            : r,
        ),
      )
      message.error(`保存失败：${(err as Error).message}`)
    } finally {
      setPending((p) => {
        const next = { ...p }
        delete next[cellKey]
        return next
      })
    }
  }

  const columns = useMemo<ColumnsType<TenantFeatureEntitlementMatrixRow>>(() => {
    const tenantCol: ColumnType<TenantFeatureEntitlementMatrixRow> = {
      title: '租户名称',
      dataIndex: 'workspace_name',
      key: 'workspace_name',
      fixed: 'left',
      width: 220,
      render: (_: unknown, row) => (
        <div className="tfem-tenant-cell">
          <div className="tfem-tenant-name">{row.workspace_name}</div>
          <div className="tfem-tenant-id">{row.workspace_id}</div>
        </div>
      ),
    }

    const featureGroupCols: ColumnGroupType<TenantFeatureEntitlementMatrixRow>[] = groups.map((group) => ({
      title: group.label,
      key: group.key,
      children: group.features.map((feature) => ({
        title: feature.label,
        key: feature.key,
        align: 'center' as const,
        width: 128,
        render: (_: unknown, row: TenantFeatureEntitlementMatrixRow) => {
          const cellKey = `${row.workspace_id}:${feature.key}`
          const checked = Boolean(row.entitlements[feature.key])
          const isPending = Boolean(pending[cellKey])
          const checkbox = (
            <Checkbox
              data-testid={`tfem-checkbox-${row.workspace_id}-${feature.key}`}
              checked={checked}
              disabled={isPending}
              onChange={(e) => {
                const next = e.target.checked
                // Light confirmation only for revoking an already-enabled
                // feature (spec §14) — granting never needs a confirm.
                if (!next) return
                void toggleFeature(row, feature.key, next)
              }}
            />
          )
          if (!checked) return checkbox
          return (
            <Popconfirm
              title="取消该功能授权？"
              description={`租户「${row.workspace_name}」将立即失去「${feature.label}」的使用权限。`}
              okText="确认关闭"
              cancelText="取消"
              onConfirm={() => void toggleFeature(row, feature.key, false)}
            >
              <span>
                <Checkbox data-testid={`tfem-checkbox-${row.workspace_id}-${feature.key}`} checked disabled={isPending} />
              </span>
            </Popconfirm>
          )
        },
      })),
    }))

    return [tenantCol, ...featureGroupCols]
  }, [groups, pending])

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          租户功能授权矩阵
        </Typography.Title>
        <Typography.Text type="secondary">为租户配置可使用的 Vistoera 功能。当前由平台管理员人工授权。</Typography.Text>
      </div>

      {error && <Alert type="error" message={error} showIcon style={{ margin: '12px 0' }} />}

      <SectionCard
        title="租户 × 功能"
        extra={
          <Search
            placeholder="搜索租户名称或 ID"
            allowClear
            onSearch={onSearch}
            style={{ width: 280 }}
            data-testid="tfem-search"
          />
        }
      >
        <Table
          className="pf-data-table tfem-table"
          rowKey="workspace_id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          sticky
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description="暂无租户" /> }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (t) => `共 ${t} 条`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage)
              setPageSize(nextPageSize)
            },
          }}
        />
      </SectionCard>
    </div>
  )
}
